import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
  Launch as LaunchIcon,
  Publish as PublishIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import type { Plan, Service } from '../lib/db/types';

interface WebsiteSocialLinks {
  instagram: string;
  facebook: string;
  youtube: string;
  patreon: string;
}

interface WebsiteAddress {
  streetAddress: string;
  addressLocality: string;
  addressRegion: string;
  postalCode: string;
  addressCountry: string;
}

interface WebsiteGeo {
  latitude: string;
  longitude: string;
}

interface WebsiteOpeningHours {
  dayOfWeek: string;
  opens: string;
  closes: string;
}

interface WebsiteGeneralInfo {
  name: string;
  shortName: string;
  description: string;
  url: string;
  logo: string;
  image: string;
  telephone: string;
  phoneDisplay: string;
  phoneTel: string;
  email: string;
  location: string;
  mapsEmbed: string;
  socialLinks: WebsiteSocialLinks;
  address: WebsiteAddress;
  geo: WebsiteGeo;
  openingHoursSpecification: WebsiteOpeningHours[];
}

interface WebsiteCoach {
  name: string;
  rank: string;
  role: string;
  bio: string;
  specialties: string[];
  photo: string;
}

interface WebsiteValue {
  title: string;
  icon: string;
  description: string;
}

interface WebsiteFaq {
  question: string;
  answer: string;
}

interface WebsiteSection {
  id: string;
  visible: boolean;
}

interface WebsiteDraft {
  templateId: WebsiteTemplateId;
  general: WebsiteGeneralInfo;
  coaches: WebsiteCoach[];
  values: WebsiteValue[];
  faqs: WebsiteFaq[];
  sections: WebsiteSection[];
}

interface LegacyWebsiteDraft extends Partial<WebsiteDraft> {
  description?: string;
  heroImageUrl?: string;
  mapsEmbed?: string;
  seoTitle?: string;
  seoDescription?: string;
  socialLinks?: Partial<WebsiteSocialLinks>;
}

interface PublishedWebsite {
  status?: string;
  publishedAt?: { toDate?: () => Date };
}

interface LocationDoc {
  id: string;
  name?: string;
  address?: string;
  location?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
  };
}

interface ChecklistItem {
  label: string;
  complete: boolean;
}

type ImageUploadTarget =
  | { type: 'general'; field: 'logo' }
  | { type: 'coach'; index: number; field: 'photo' };

type WebsiteTemplateId = 'martial-arts';

const websiteTemplates: Array<{ id: WebsiteTemplateId; label: string; description: string }> = [
  {
    id: 'martial-arts',
    label: 'Martial arts',
    description: 'A full gym website template based on the AGA martial arts site.',
  },
];

const defaultValues: WebsiteValue[] = [
  {
    title: 'Always be learning',
    icon: '📚',
    description: 'The day that you stop learning, is the day that creativity and interest dies. I encourage students and coaches to be always learning, even if it does not relate to the sport.',
  },
  {
    title: 'Take accountability',
    icon: '✓',
    description: "Don't blame others, blame yourself. Accountability is will allow you to fix your mistakes and to keep yourself honest",
  },
  {
    title: 'Do More',
    icon: '💪',
    description: 'Many times our problems are not from a lack of knowledge, but a lack of application. Great things happen when you try to do more than the day before.',
  },
  {
    title: 'Have fun',
    icon: '😄',
    description: "Don't take things too seriously, make sure that at the end of the day that you are having a blast",
  },
];

const defaultFaqs: WebsiteFaq[] = [
  {
    question: 'Do I need any experience to start?',
    answer: 'No experience is necessary! Most of our students start with zero martial arts background. Our fundamentals classes are specifically designed for beginners, and our instructors will guide you every step of the way.',
  },
  {
    question: 'What should I wear to my first class?',
    answer: 'For your first class, wear comfortable athletic clothing (t-shirt and shorts or athletic pants). We have loaner gis available. Once you decide to continue, we can help you purchase your own gi.',
  },
  {
    question: 'How often should I train?',
    answer: "We recommend training at least 2-3 times per week for consistent progress. However, even once a week will provide benefits. Our unlimited membership allows you to train as often as you'd like.",
  },
  {
    question: 'Is BJJ safe?',
    answer: 'Yes! Brazilian Jiu-Jitsu is one of the safest martial arts. We emphasize controlled training, proper technique, and respect for training partners. Our instructors closely supervise all classes to ensure a safe training environment.',
  },
  {
    question: 'What age do you accept for kids classes?',
    answer: 'We accept kids starting at age 7 in our kids program. We have age-appropriate classes for different skill levels and maturity. Teens 15+ can join adult classes with instructor approval.',
  },
  {
    question: 'Can I freeze my membership?',
    answer: 'Yes, we offer membership freezes for medical reasons, travel, or other circumstances. Please speak with our staff about freeze policies and any applicable fees.',
  },
];

const defaultSections: WebsiteSection[] = [
  { id: 'schedule', visible: true },
  { id: 'pricing', visible: true },
  { id: 'coaches', visible: true },
  { id: 'values', visible: true },
  { id: 'faqs', visible: true },
  { id: 'location', visible: true },
];

const emptyGeneral: WebsiteGeneralInfo = {
  name: '',
  shortName: '',
  description: '',
  url: '',
  logo: '',
  image: '',
  telephone: '',
  phoneDisplay: '',
  phoneTel: '',
  email: '',
  location: '',
  mapsEmbed: '',
  socialLinks: {
    instagram: '',
    facebook: '',
    youtube: '',
    patreon: '',
  },
  address: {
    streetAddress: '',
    addressLocality: '',
    addressRegion: '',
    postalCode: '',
    addressCountry: 'US',
  },
  geo: {
    latitude: '',
    longitude: '',
  },
  openingHoursSpecification: [
    { dayOfWeek: 'Monday-Friday', opens: '11:00', closes: '21:00' },
    { dayOfWeek: 'Saturday', opens: '10:45', closes: '14:00' },
  ],
};

const defaultDraft: WebsiteDraft = {
  templateId: 'martial-arts',
  general: emptyGeneral,
  coaches: [],
  values: defaultValues,
  faqs: defaultFaqs,
  sections: defaultSections,
};

function isFilled(value: unknown) {
  return typeof value === 'string' ? value.trim().length > 0 : !!value;
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function phoneTel(value?: string) {
  if (!value) return '';
  const digits = value.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return digits;
  return digits ? `+1${digits}` : '';
}

function displayPhone(value?: string) {
  const digits = value?.replace(/\D/g, '') || '';
  if (digits.length !== 10) return value || '';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function locationLabel(city?: string, region?: string) {
  return [city, region].filter(Boolean).join(', ');
}

function addressLocationLabel(address: WebsiteAddress) {
  return locationLabel(address.addressLocality, address.addressRegion);
}

function buildGeneralDefaults(activeTenant: ReturnType<typeof useTenant>['activeTenant'], location?: LocationDoc): WebsiteGeneralInfo {
  const address = activeTenant?.businessProfile?.address;
  const locationAddress = location?.location;
  const line1 = address?.line1 || locationAddress?.addressLine1 || '';
  const city = address?.city || locationAddress?.city || '';
  const region = address?.region || locationAddress?.region || '';
  const postalCode = address?.postalCode || locationAddress?.postalCode || '';
  const country = address?.country || locationAddress?.country || 'US';
  const phone = activeTenant?.businessProfile?.phone || '';

  return {
    ...emptyGeneral,
    name: activeTenant?.name || '',
    shortName: activeTenant?.name ? initials(activeTenant.name) : '',
    url: activeTenant?.businessProfile?.website || (activeTenant?.slug ? `/sites/${activeTenant.slug}` : ''),
    logo: activeTenant?.branding?.logoUrl || '',
    image: activeTenant?.branding?.logoUrl || '',
    telephone: phone,
    phoneDisplay: displayPhone(phone),
    phoneTel: phoneTel(phone),
    email: activeTenant?.businessProfile?.email || '',
    location: locationLabel(city, region) || location?.address || '',
    address: {
      streetAddress: line1,
      addressLocality: city,
      addressRegion: region,
      postalCode,
      addressCountry: country,
    },
  };
}

function mergeDraft(value: LegacyWebsiteDraft | null | undefined, activeTenant: ReturnType<typeof useTenant>['activeTenant'], location?: LocationDoc): WebsiteDraft {
  const generalDefaults = buildGeneralDefaults(activeTenant, location);
  const legacyGeneral: Partial<WebsiteGeneralInfo> = {
    description: value?.description,
    image: value?.heroImageUrl,
    mapsEmbed: value?.mapsEmbed,
    socialLinks: value?.socialLinks as WebsiteSocialLinks | undefined,
  };

  return {
    ...defaultDraft,
    ...(value || {}),
    general: {
      ...generalDefaults,
      ...(value?.general || {}),
      ...Object.fromEntries(Object.entries(legacyGeneral).filter(([, fieldValue]) => fieldValue !== undefined)),
      socialLinks: {
        ...generalDefaults.socialLinks,
        ...(value?.general?.socialLinks || {}),
        ...(value?.socialLinks || {}),
      },
      address: {
        ...generalDefaults.address,
        ...(value?.general?.address || {}),
      },
      geo: {
        ...generalDefaults.geo,
        ...(value?.general?.geo || {}),
      },
      openingHoursSpecification: value?.general?.openingHoursSpecification?.length
        ? value.general.openingHoursSpecification
        : generalDefaults.openingHoursSpecification,
    },
    coaches: value?.coaches || defaultDraft.coaches,
    values: value?.values === undefined ? defaultValues : value.values,
    faqs: value?.faqs === undefined ? defaultFaqs : value.faqs,
    sections: value?.sections?.length ? value.sections : defaultSections,
  };
}

function formatCurrency(cents?: number) {
  if (typeof cents !== 'number') return 'No price set';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function sectionLabel(sectionId: string) {
  return sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
}

function fileExtension(file: File) {
  const nameExtension = file.name.split('.').pop()?.trim().toLowerCase();
  if (nameExtension && /^[a-z0-9]+$/.test(nameExtension)) {
    return nameExtension;
  }

  const mimeExtension = file.type.split('/')[1]?.split('+')[0]?.trim().toLowerCase();
  return mimeExtension && /^[a-z0-9]+$/.test(mimeExtension) ? mimeExtension : 'png';
}

function readFileBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const [, base64 = ''] = result.split(',');
      if (!base64) {
        reject(new Error('Could not read image file'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read image file'));
    reader.readAsDataURL(file);
  });
}

function formatTimestamp(timestamp?: { toDate?: () => Date }) {
  return timestamp?.toDate?.()?.toLocaleString() || 'Not published yet';
}

function publicWebsiteUrl(publicPath: string) {
  if (!publicPath || typeof window === 'undefined') return publicPath;
  const { protocol, hostname, port, origin } = window.location;
  const isLocalDev = ['localhost', '127.0.0.1', '::1'].includes(hostname) && port !== '5002';
  if (isLocalDev) return `${protocol}//${hostname}:5002${publicPath}`;
  return `${origin}${publicPath}`;
}

function checklistProgress(items: ChecklistItem[]) {
  const complete = items.filter((item) => item.complete).length;
  return {
    complete,
    total: items.length,
    done: items.length > 0 && complete === items.length,
  };
}

function sectionIsVisible(sections: WebsiteSection[], sectionId: string) {
  return sections.find((section) => section.id === sectionId)?.visible !== false;
}

export const WebsiteDashboardPage = () => {
  const { tenantId, activeTenant } = useTenant();
  const [draft, setDraft] = useState<WebsiteDraft>(defaultDraft);
  const [activeStep, setActiveStep] = useState(0);
  const [services, setServices] = useState<(Service & { id: string })[]>([]);
  const [plans, setPlans] = useState<(Plan & { id: string })[]>([]);
  const [published, setPublished] = useState<PublishedWebsite | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploadingImageKey, setUploadingImageKey] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const publicPath = activeTenant?.slug ? `/sites/${activeTenant.slug}` : '';
  const publicUrl = publicWebsiteUrl(publicPath);
  const bookingUrl = activeTenant?.slug ? `/book/${activeTenant.slug}` : '';

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    const currentTenantId = tenantId;
    let mounted = true;
    async function loadWebsiteBuilder() {
      setLoading(true);
      setMessage(null);

      try {
        const [draftSnap, publishedSnap, locationsSnap, servicesSnap, plansSnap] = await Promise.all([
          getDoc(doc(db, 'customers', currentTenantId, 'website', 'draft')),
          getDoc(doc(db, 'customers', currentTenantId, 'website', 'published')),
          getDocs(query(collection(db, 'locations'), where('tenantId', '==', currentTenantId), limit(1))),
          getDocs(
            query(
              collection(db, 'services'),
              where('tenantId', '==', currentTenantId),
              where('visibility', '==', 'public'),
              where('isActive', '==', true),
              orderBy('displayOrder')
            )
          ),
          getDocs(query(collection(db, 'plans'), where('tenantId', '==', currentTenantId), where('active', '==', true))),
        ]);

        if (!mounted) return;
        const loadedLocations = locationsSnap.docs.map((locationDoc) => ({ id: locationDoc.id, ...locationDoc.data() } as LocationDoc));
        setDraft(mergeDraft(draftSnap.exists() ? (draftSnap.data() as LegacyWebsiteDraft) : null, activeTenant, loadedLocations[0]));
        setPublished(publishedSnap.exists() ? (publishedSnap.data() as PublishedWebsite) : null);
        setServices(servicesSnap.docs.map((serviceDoc) => ({ id: serviceDoc.id, ...serviceDoc.data() } as Service & { id: string })));
        setPlans(plansSnap.docs.map((planDoc) => ({ id: planDoc.id, ...planDoc.data() } as Plan & { id: string })));
      } catch (error: any) {
        console.error('Error loading website builder:', error);
        if (mounted) setMessage({ type: 'error', text: error.message || 'Failed to load website builder.' });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadWebsiteBuilder();
    return () => {
      mounted = false;
    };
  }, [activeTenant, tenantId]);

  const scheduleCount = useMemo(
    () => services.reduce((total, service) => total + (service.recurringClassTimes?.filter((time) => time.isActive !== false).length || 0), 0),
    [services]
  );
  const scheduleVisible = sectionIsVisible(draft.sections, 'schedule');
  const pricingVisible = sectionIsVisible(draft.sections, 'pricing');
  const coachesVisible = sectionIsVisible(draft.sections, 'coaches');
  const valuesVisible = sectionIsVisible(draft.sections, 'values');
  const faqsVisible = sectionIsVisible(draft.sections, 'faqs');
  const locationVisible = sectionIsVisible(draft.sections, 'location');
  const generalItems: ChecklistItem[] = [
    { label: 'Template', complete: websiteTemplates.some((template) => template.id === draft.templateId) },
    { label: 'Name', complete: isFilled(draft.general.name) },
    { label: 'Short name', complete: isFilled(draft.general.shortName) },
    { label: 'Description', complete: isFilled(draft.general.description) },
    { label: 'URL', complete: isFilled(draft.general.url) },
    { label: 'Logo', complete: isFilled(draft.general.logo) },
    { label: 'Telephone', complete: isFilled(draft.general.telephone) },
    { label: 'Email', complete: isFilled(draft.general.email) },
    { label: 'Map embed', complete: isFilled(draft.general.mapsEmbed) },
    { label: 'Address', complete: Object.values(draft.general.address).every(isFilled) },
    { label: 'Opening hours', complete: draft.general.openingHoursSpecification.length > 0 && draft.general.openingHoursSpecification.every((hours) => isFilled(hours.dayOfWeek) && isFilled(hours.opens) && isFilled(hours.closes)) },
  ];
  const coachItems: ChecklistItem[] = coachesVisible
    ? [
        { label: 'At least one coach', complete: draft.coaches.length > 0 },
        { label: 'Coach details', complete: draft.coaches.length > 0 && draft.coaches.every((coach) => isFilled(coach.name) && isFilled(coach.rank) && isFilled(coach.role) && isFilled(coach.bio) && isFilled(coach.photo) && coach.specialties.length > 0) },
      ]
    : [{ label: 'Coach section hidden', complete: true }];
  const valueItems: ChecklistItem[] = valuesVisible
    ? [
        { label: 'At least one value', complete: draft.values.length > 0 },
        { label: 'Value details', complete: draft.values.length > 0 && draft.values.every((value) => isFilled(value.title) && isFilled(value.icon) && isFilled(value.description)) },
      ]
    : [{ label: 'Values section hidden', complete: true }];
  const pricingItems: ChecklistItem[] = [
    { label: 'Pricing section choice', complete: !pricingVisible || plans.length > 0 },
    { label: 'Active plans', complete: plans.length > 0 },
  ];
  const faqItems: ChecklistItem[] = faqsVisible
    ? [
        { label: 'At least one FAQ', complete: draft.faqs.length > 0 },
        { label: 'FAQ details', complete: draft.faqs.length > 0 && draft.faqs.every((faq) => isFilled(faq.question) && isFilled(faq.answer)) },
      ]
    : [{ label: 'FAQ section hidden', complete: true }];
  const scheduleItems: ChecklistItem[] = [
    { label: 'Schedule section choice', complete: !scheduleVisible || scheduleCount > 0 },
    { label: 'Class schedule source', complete: scheduleCount > 0 },
    { label: 'Location section choice', complete: !locationVisible || isFilled(draft.general.location) },
  ];
  const steps = [
    { label: 'Business Info', items: generalItems },
    { label: 'Coaches Bio', items: coachItems },
    { label: 'Values', items: valueItems },
    { label: 'Pricing', items: pricingItems },
    { label: 'FAQ', items: faqItems },
    { label: 'Schedule', items: scheduleItems },
  ];
  const activeProgress = checklistProgress(steps[activeStep].items);

  const handleGeneralChange = (field: keyof WebsiteGeneralInfo, value: string) => {
    setDraft((current) => ({
      ...current,
      general: {
        ...current.general,
        [field]: value,
      },
    }));
  };

  const handleTelephoneChange = (value: string) => {
    setDraft((current) => ({
      ...current,
      general: {
        ...current.general,
        telephone: value,
        phoneDisplay: displayPhone(value),
        phoneTel: phoneTel(value),
      },
    }));
  };

  const handleLogoChange = (value: string) => {
    setDraft((current) => ({
      ...current,
      general: {
        ...current.general,
        logo: value,
        image: value,
      },
    }));
  };

  const handleSocialChange = (field: keyof WebsiteSocialLinks, value: string) => {
    setDraft((current) => ({
      ...current,
      general: {
        ...current.general,
        socialLinks: {
          ...current.general.socialLinks,
          [field]: value,
        },
      },
    }));
  };

  const handleAddressChange = (field: keyof WebsiteAddress, value: string) => {
    setDraft((current) => ({
      ...current,
      general: {
        ...current.general,
        address: {
          ...current.general.address,
          [field]: value,
        },
      },
    }));
  };

  const handleHoursChange = (index: number, field: keyof WebsiteOpeningHours, value: string) => {
    setDraft((current) => ({
      ...current,
      general: {
        ...current.general,
        openingHoursSpecification: current.general.openingHoursSpecification.map((hours, hoursIndex) => (
          hoursIndex === index ? { ...hours, [field]: value } : hours
        )),
      },
    }));
  };

  const handleCoachChange = (index: number, field: keyof WebsiteCoach, value: string) => {
    setDraft((current) => ({
      ...current,
      coaches: current.coaches.map((coach, coachIndex) => (
        coachIndex === index
          ? { ...coach, [field]: field === 'specialties' ? value.split('\n').map((item) => item.trim()).filter(Boolean) : value }
          : coach
      )),
    }));
  };

  const uploadWebsiteImage = async (event: ChangeEvent<HTMLInputElement>, target: ImageUploadTarget, uploadKey: string) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !tenantId) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please choose an image file.' });
      return;
    }

    setUploadingImageKey(uploadKey);
    setMessage(null);

    try {
      const uploadWebsiteImageCallable = httpsCallable(functions, 'uploadWebsiteImage');
      const result = await uploadWebsiteImageCallable({
        tenantId,
        fileBase64: await readFileBase64(file),
        contentType: file.type,
        extension: fileExtension(file),
        fileSize: file.size,
        websiteField: uploadKey,
      });
      const downloadUrl = (result.data as { imageUrl: string }).imageUrl;

      if (target.type === 'general') {
        handleLogoChange(downloadUrl);
      } else {
        handleCoachChange(target.index, target.field, downloadUrl);
      }

      setMessage({ type: 'success', text: 'Image uploaded and added to this website section.' });
    } catch (error: any) {
      console.error('Error uploading website image:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to upload image.' });
    } finally {
      setUploadingImageKey(null);
    }
  };

  const renderImageUrlField = (
    label: string,
    value: string,
    onUrlChange: (value: string) => void,
    target: ImageUploadTarget,
    uploadKey: string
  ) => {
    const uploading = uploadingImageKey === uploadKey;
    return (
      <Stack spacing={1}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField fullWidth label={label} value={value} onChange={(event) => onUrlChange(event.target.value)} />
          <Button
            component="label"
            variant="outlined"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{ minWidth: 120 }}
          >
            Upload
            <input
              hidden
              accept="image/*"
              type="file"
              onChange={(event) => uploadWebsiteImage(event, target, uploadKey)}
            />
          </Button>
        </Stack>
        {value && (
          <Box
            component="img"
            src={value}
            alt=""
            sx={{
              width: 96,
              height: 64,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          />
        )}
      </Stack>
    );
  };

  const renderLogoUploadField = () => {
    const uploadKey = 'general-logo';
    const uploading = uploadingImageKey === uploadKey;
    const logoUrl = draft.general.logo || activeTenant?.branding?.logoUrl || '';
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" fontWeight={700}>Logo</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box
            sx={{
              width: 112,
              height: 72,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {logoUrl ? (
              <Box component="img" src={logoUrl} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <Typography variant="caption" color="text.secondary">No logo</Typography>
            )}
          </Box>
          <Button
            component="label"
            variant="outlined"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <AddIcon />}
          >
            Upload Logo
            <input
              hidden
              accept="image/*"
              type="file"
              onChange={(event) => uploadWebsiteImage(event, { type: 'general', field: 'logo' }, uploadKey)}
            />
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Uses the logo from settings unless you upload a website-specific logo here.
        </Typography>
      </Stack>
    );
  };

  const handleValueChange = (index: number, field: keyof WebsiteValue, value: string) => {
    setDraft((current) => ({
      ...current,
      values: current.values.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleFaqChange = (index: number, field: keyof WebsiteFaq, value: string) => {
    setDraft((current) => ({
      ...current,
      faqs: current.faqs.map((faq, faqIndex) => (faqIndex === index ? { ...faq, [field]: value } : faq)),
    }));
  };

  const toggleSection = (sectionId: string) => {
    setDraft((current) => ({
      ...current,
      sections: current.sections.map((section) => (
        section.id === sectionId ? { ...section, visible: !section.visible } : section
      )),
    }));
  };

  const saveDraft = async () => {
    if (!tenantId) return;
    const logo = draft.general.logo || activeTenant?.branding?.logoUrl || '';
    const normalizedDraft: WebsiteDraft = {
      ...draft,
      general: {
        ...draft.general,
        logo,
        image: logo,
        location: addressLocationLabel(draft.general.address),
        phoneDisplay: displayPhone(draft.general.telephone),
        phoneTel: phoneTel(draft.general.telephone),
      },
    };
    await setDoc(
      doc(db, 'customers', tenantId, 'website', 'draft'),
      {
        ...normalizedDraft,
        tenantId,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    setDraft(normalizedDraft);
  };

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setMessage(null);
    try {
      await saveDraft();
      setMessage({ type: 'success', text: 'Website draft saved.' });
    } catch (error: any) {
      console.error('Error saving website draft:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save website draft.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!tenantId) return;
    setPublishing(true);
    setMessage(null);
    try {
      await saveDraft();
      const publishWebsite = httpsCallable(functions, 'publishWebsite');
      await publishWebsite({ tenantId });
      const publishedSnap = await getDoc(doc(db, 'customers', tenantId, 'website', 'published'));
      setPublished(publishedSnap.exists() ? (publishedSnap.data() as PublishedWebsite) : null);
      setMessage({ type: 'success', text: 'Website published.' });
    } catch (error: any) {
      console.error('Error publishing website:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to publish website.' });
    } finally {
      setPublishing(false);
    }
  };

  if (!tenantId || loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Website Builder
            </Typography>
            <Typography color="text.secondary">
              Build each website section one step at a time. Fitstac pre-fills what it already knows, and each step gets a checkmark once its required information is complete.
            </Typography>
          </Box>

          {message && (
            <Alert severity={message.type} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          <Paper sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">Publishing</Typography>
                <Typography variant="body2" color="text.secondary">
                  Live URL: {publicUrl ? <Link href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</Link> : 'Unavailable until the tenant has a slug'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last published: {formatTimestamp(published?.publishedAt)}
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
                <InputLabel id="website-template-label">Template</InputLabel>
                <Select
                  labelId="website-template-label"
                  label="Template"
                  value={draft.templateId}
                  onChange={(event) => setDraft((current) => ({ ...current, templateId: event.target.value as WebsiteTemplateId }))}
                >
                  {websiteTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" startIcon={<SaveIcon />} disabled={saving || publishing} onClick={() => void handleSave()}>
                {saving ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button variant="contained" startIcon={<PublishIcon />} disabled={saving || publishing} onClick={() => void handlePublish()}>
                {publishing ? 'Publishing...' : 'Publish'}
              </Button>
              <Button variant="outlined" startIcon={<LaunchIcon />} disabled={!published || !publicUrl} href={publicUrl} target="_blank">
                View Site
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stepper nonLinear activeStep={activeStep} alternativeLabel>
              {steps.map((step, index) => {
                const progress = checklistProgress(step.items);
                return (
                  <Step key={step.label} completed={progress.done}>
                    <StepButton onClick={() => setActiveStep(index)}>
                      <StepLabel
                        icon={progress.done ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon color={index === activeStep ? 'primary' : 'disabled'} />}
                      >
                        <Stack spacing={0.25} alignItems="center">
                          <Typography variant="body2" fontWeight={700}>{step.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{progress.complete}/{progress.total}</Typography>
                        </Stack>
                      </StepLabel>
                    </StepButton>
                  </Step>
                );
              })}
            </Stepper>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" fontWeight={700}>
                    Section {activeStep + 1}: {steps[activeStep].label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeProgress.done ? 'This section is complete.' : 'Fill the missing fields in this section to earn the checkmark.'}
                  </Typography>
                </Box>
                <Chip
                  color={activeProgress.done ? 'success' : 'default'}
                  icon={activeProgress.done ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                  label={activeProgress.done ? 'Complete' : `${activeProgress.complete}/${activeProgress.total}`}
                />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {steps[activeStep].items.filter((item) => !item.complete).map((item) => (
                  <Chip key={item.label} variant="outlined" size="small" label={item.label} />
                ))}
              </Stack>

              {activeStep === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Name" value={draft.general.name} onChange={(event) => handleGeneralChange('name', event.target.value)} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="ShortName" value={draft.general.shortName} onChange={(event) => handleGeneralChange('shortName', event.target.value)} /></Grid>
                  <Grid item xs={12}><TextField fullWidth multiline minRows={4} label="Description" value={draft.general.description} onChange={(event) => handleGeneralChange('description', event.target.value)} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="URL" value={draft.general.url} onChange={(event) => handleGeneralChange('url', event.target.value)} /></Grid>
                  <Grid item xs={12} md={6}>
                    {renderLogoUploadField()}
                  </Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Telephone" value={draft.general.telephone} onChange={(event) => handleTelephoneChange(event.target.value)} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Email" value={draft.general.email} onChange={(event) => handleGeneralChange('email', event.target.value)} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Maps embed URL" value={draft.general.mapsEmbed} onChange={(event) => handleGeneralChange('mapsEmbed', event.target.value)} /></Grid>
                  {Object.entries(draft.general.socialLinks).map(([key, value]) => (
                    <Grid item xs={12} md={3} key={key}>
                      <TextField fullWidth label={sectionLabel(key)} value={value} onChange={(event) => handleSocialChange(key as keyof WebsiteSocialLinks, event.target.value)} />
                    </Grid>
                  ))}
                  <Grid item xs={12}><Divider><Chip label="Address" /></Divider></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Street address" value={draft.general.address.streetAddress} onChange={(event) => handleAddressChange('streetAddress', event.target.value)} /></Grid>
                  <Grid item xs={12} md={2}><TextField fullWidth label="City" value={draft.general.address.addressLocality} onChange={(event) => handleAddressChange('addressLocality', event.target.value)} /></Grid>
                  <Grid item xs={12} md={2}><TextField fullWidth label="State" value={draft.general.address.addressRegion} onChange={(event) => handleAddressChange('addressRegion', event.target.value)} /></Grid>
                  <Grid item xs={12} md={2}><TextField fullWidth label="Postal code" value={draft.general.address.postalCode} onChange={(event) => handleAddressChange('postalCode', event.target.value)} /></Grid>
                  <Grid item xs={12} md={2}><TextField fullWidth label="Country" value={draft.general.address.addressCountry} onChange={(event) => handleAddressChange('addressCountry', event.target.value)} /></Grid>
                  <Grid item xs={12}><Divider><Chip label="Opening hours" /></Divider></Grid>
                  {draft.general.openingHoursSpecification.map((hours, index) => (
                    <Grid item xs={12} key={`hours-${index}`}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={5}><TextField fullWidth label="Day of week" value={hours.dayOfWeek} onChange={(event) => handleHoursChange(index, 'dayOfWeek', event.target.value)} /></Grid>
                          <Grid item xs={12} md={3}><TextField fullWidth type="time" label="Opens" InputLabelProps={{ shrink: true }} value={hours.opens} onChange={(event) => handleHoursChange(index, 'opens', event.target.value)} /></Grid>
                          <Grid item xs={12} md={3}><TextField fullWidth type="time" label="Closes" InputLabelProps={{ shrink: true }} value={hours.closes} onChange={(event) => handleHoursChange(index, 'closes', event.target.value)} /></Grid>
                          <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton onClick={() => setDraft((current) => ({ ...current, general: { ...current.general, openingHoursSpecification: current.general.openingHoursSpecification.filter((_, hoursIndex) => hoursIndex !== index) } }))}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button startIcon={<AddIcon />} onClick={() => setDraft((current) => ({ ...current, general: { ...current.general, openingHoursSpecification: [...current.general.openingHoursSpecification, { dayOfWeek: '', opens: '09:00', closes: '17:00' }] } }))}>
                      Add Hours
                    </Button>
                  </Grid>
                </Grid>
              )}

              {activeStep === 1 && (
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Coaches</Typography>
                    <Button startIcon={<AddIcon />} onClick={() => setDraft((current) => ({ ...current, coaches: [...current.coaches, { name: '', rank: '', role: '', bio: '', specialties: [], photo: '' }] }))}>
                      Add Coach
                    </Button>
                  </Stack>
                  {draft.coaches.map((coach, index) => (
                    <Paper key={`coach-${index}`} variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Name" value={coach.name} onChange={(event) => handleCoachChange(index, 'name', event.target.value)} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Rank" value={coach.rank} onChange={(event) => handleCoachChange(index, 'rank', event.target.value)} /></Grid>
                        <Grid item xs={12} md={3}><TextField fullWidth label="Role" value={coach.role} onChange={(event) => handleCoachChange(index, 'role', event.target.value)} /></Grid>
                        <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}><IconButton onClick={() => setDraft((current) => ({ ...current, coaches: current.coaches.filter((_, coachIndex) => coachIndex !== index) }))}><DeleteOutlineIcon /></IconButton></Grid>
                        <Grid item xs={12} md={6}>
                          {renderImageUrlField(
                            'Photo URL',
                            coach.photo,
                            (value) => handleCoachChange(index, 'photo', value),
                            { type: 'coach', index, field: 'photo' },
                            `coach-${index}-photo`
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth multiline minRows={2} label="Specialties, one per line" value={coach.specialties.join('\n')} onChange={(event) => handleCoachChange(index, 'specialties', event.target.value)} /></Grid>
                        <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Bio" value={coach.bio} onChange={(event) => handleCoachChange(index, 'bio', event.target.value)} /></Grid>
                      </Grid>
                    </Paper>
                  ))}
                  {draft.coaches.length === 0 && <Typography color="text.secondary">No coaches added yet.</Typography>}
                </Stack>
              )}

              {activeStep === 2 && (
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Values</Typography>
                    <Button startIcon={<AddIcon />} onClick={() => setDraft((current) => ({ ...current, values: [...current.values, { title: '', icon: '', description: '' }] }))}>
                      Add Value
                    </Button>
                  </Stack>
                  {draft.values.map((value, index) => (
                    <Paper key={`value-${index}`} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1}>
                          <TextField fullWidth label="Title" value={value.title} onChange={(event) => handleValueChange(index, 'title', event.target.value)} />
                          <TextField label="Icon" value={value.icon} onChange={(event) => handleValueChange(index, 'icon', event.target.value)} sx={{ width: 110 }} />
                          <IconButton onClick={() => setDraft((current) => ({ ...current, values: current.values.filter((_, valueIndex) => valueIndex !== index) }))}><DeleteOutlineIcon /></IconButton>
                        </Stack>
                        <TextField fullWidth multiline minRows={2} label="Description" value={value.description} onChange={(event) => handleValueChange(index, 'description', event.target.value)} />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}

              {activeStep === 3 && (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="h6">Pricing</Typography>
                    <Chip label={pricingVisible ? 'Visible' : 'Hidden'} color={pricingVisible ? 'primary' : 'default'} onClick={() => toggleSection('pricing')} />
                  </Stack>
                  <Typography color="text.secondary">
                    Pricing is generated from active Fitstac plans so you do not type prices twice.
                  </Typography>
                  <Grid container spacing={2}>
                    {plans.map((plan) => (
                      <Grid item xs={12} md={4} key={plan.id}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography fontWeight={700}>{plan.name}</Typography>
                          <Typography color="primary" fontWeight={700}>{formatCurrency(plan.price)}</Typography>
                          <Typography variant="body2" color="text.secondary">{plan.description}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                    {plans.length === 0 && <Grid item xs={12}><Typography color="text.secondary">No active plans yet.</Typography></Grid>}
                  </Grid>
                </Stack>
              )}

              {activeStep === 4 && (
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">FAQ</Typography>
                    <Button startIcon={<AddIcon />} onClick={() => setDraft((current) => ({ ...current, faqs: [...current.faqs, { question: '', answer: '' }] }))}>
                      Add FAQ
                    </Button>
                  </Stack>
                  {draft.faqs.map((faq, index) => (
                    <Paper key={`faq-${index}`} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1}>
                          <TextField fullWidth label="Question" value={faq.question} onChange={(event) => handleFaqChange(index, 'question', event.target.value)} />
                          <IconButton onClick={() => setDraft((current) => ({ ...current, faqs: current.faqs.filter((_, faqIndex) => faqIndex !== index) }))}><DeleteOutlineIcon /></IconButton>
                        </Stack>
                        <TextField fullWidth multiline minRows={2} label="Answer" value={faq.answer} onChange={(event) => handleFaqChange(index, 'answer', event.target.value)} />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}

              {activeStep === 5 && (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="h6">Schedule</Typography>
                    <Chip label={scheduleVisible ? 'Schedule visible' : 'Schedule hidden'} color={scheduleVisible ? 'primary' : 'default'} onClick={() => toggleSection('schedule')} />
                    <Chip label={locationVisible ? 'Location visible' : 'Location hidden'} color={locationVisible ? 'primary' : 'default'} onClick={() => toggleSection('location')} />
                  </Stack>
                  <Typography color="text.secondary">
                    Schedule is derived from active public services and recurring class times.
                  </Typography>
                  <Stack spacing={1.5}>
                    <Chip label={`Public services: ${services.length}`} />
                    <Chip label={`Schedule entries: ${scheduleCount}`} />
                    <Chip label={`Booking CTA: ${bookingUrl || 'Not set'}`} />
                  </Stack>
                </Stack>
              )}

              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Button disabled={activeStep === 0} onClick={() => setActiveStep((current) => Math.max(0, current - 1))}>
                  Back
                </Button>
                <Button variant="contained" disabled={activeStep === steps.length - 1} onClick={() => setActiveStep((current) => Math.min(steps.length - 1, current + 1))}>
                  Next Section
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
};

export default WebsiteDashboardPage;
